import { noop, stubFalse } from 'lodash';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
  ActionButtonHandle,
  DetailContext,
  DetailHandle,
  EmptyComponent,
  FormPanel,
  TableField,
  TableFieldHandle,
  TableFieldToolbarProps,
  useFormSelector,
} from '@eas/common-web';
import { Delimeter } from '@components/form/delimeter/delimeter';
import { useAutoCollapse } from '@components/form/misc/auto-collapse-hook';
import { FormTableCount } from '@components/form/table-field/form-table-count';
import { Document, DocumentAttachment } from '@models';
import { AttachmentType, DocumentType, UIElement } from '@enums';
import { useAttachmentColumns } from './attachment-columns';
import { useAttachmentContext } from './attachment-context';
import { AttachmentDialog } from './attachment-dialog';
import { AttachmentToolbar } from './attachment-toolbar';

export interface AttachmentFieldProps {
  attachmentType?: AttachmentType;
  attachments: DocumentAttachment[];
  label: React.ReactNode;
  disabled?: boolean;
  defaultName: string;
  showToolbar?: boolean;
  documentId: string;
  hasPermission: (element: string) => boolean;
  onAddedAttachment?: (item: DocumentAttachment) => Promise<void>;
  showAddButton?: boolean;
  showEditButton?: boolean;
  showDeleteButton?: boolean;
  showRevalidateButton?: boolean;
  showDownloadReportButton?: boolean;
  disableEditButton?: (attachment: DocumentAttachment) => boolean;
  defaultExpanded?: boolean;
  addPermission?: string;
  addBulkPermission?: string;
  copyPermission?: string;
  copyDraftPermission?: string;
  editPermission?: string;
  deletePermission?: string;
  revalidatePermission?: string;
  downloadReportPermission?: string;
  convertToPdfPermission?: string;
  signPermission?: string;
  envelopesField?: boolean;
}

export function AttachmentField({
  attachmentType,
  attachments,
  documentId,
  label,
  defaultName = '',
  showToolbar = true,
  showAddButton = true,
  showEditButton = true,
  showDeleteButton = true,
  showRevalidateButton = true,
  showDownloadReportButton = true,
  disableEditButton = stubFalse,
  defaultExpanded,
  hasPermission,
  disabled = false,
  onAddedAttachment,
  addPermission,
  addBulkPermission,
  copyPermission,
  copyDraftPermission,
  editPermission,
  deletePermission,
  revalidatePermission,
  downloadReportPermission,
  convertToPdfPermission,
  signPermission,
  envelopesField,
}: AttachmentFieldProps) {
  const { source, isLocked } =
    useContext<DetailHandle<Document>>(DetailContext);

  const { visitedAttachments } = useAttachmentContext();

  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const fileId = params.get('fileId');
  const { panelRef } = useAutoCollapse(attachments, defaultExpanded);

  const columns = useAttachmentColumns(attachmentType);

  const tableRef = useRef<TableFieldHandle>(null);
  const editRef = useRef<ActionButtonHandle>(null);
  const toolbarConfigRef = useRef<{
    showToolbar: boolean;
    attachmentToolbarProps: AttachmentFieldProps;
  } | null>(null);

  const hasNoPermissions = Object.keys(UIElement.Attachment).every(
    (key) => !hasPermission(key)
  );
  const disabledField =
    disabled || !showToolbar || isLocked || hasNoPermissions;
  const isDocumentInitializing =
    documentId !== '' && source.data?.id !== documentId;

  toolbarConfigRef.current = {
    showToolbar,
    attachmentToolbarProps: {
      label,
      documentId,
      attachmentType,
      attachments,
      defaultName,
      hasPermission,
      disabled: disabledField,
      onAddedAttachment,
      showAddButton,
      showEditButton,
      showDeleteButton,
      showRevalidateButton,
      showDownloadReportButton,
      disableEditButton,
      addPermission,
      addBulkPermission,
      copyPermission,
      copyDraftPermission,
      editPermission,
      deletePermission,
      revalidatePermission,
      downloadReportPermission,
      convertToPdfPermission,
      signPermission,
      envelopesField,
    },
  };

  const ToolbarComponent = useCallback((props: TableFieldToolbarProps) => {
    const { showToolbar, attachmentToolbarProps } = toolbarConfigRef.current!;

    return showToolbar ? (
      <AttachmentToolbar {...props} {...attachmentToolbarProps} ref={editRef} />
    ) : (
      <EmptyComponent />
    );
  }, []);

  return (
    <>
      <FormPanel
        label={label}
        expandable={true}
        defaultExpanded={defaultExpanded ?? false}
        ref={panelRef}
        summary={<FormTableCount items={attachments} />}
      >
        {isDocumentInitializing ? (
          <div
            style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
          >
            <CircularProgress />
          </div>
        ) : (
          <TableField<DocumentAttachment>
            ref={tableRef}
            value={attachments}
            onChange={noop}
            ToolbarComponent={ToolbarComponent}
            columns={columns}
            FormFieldsComponent={function Fields() {
              return (
                <DetailDialogFields
                  documentType={source.data?.type}
                  documentId={documentId}
                />
              );
            }}
            maxRows={15}
            disabled={disabledField}
            createRowStyle={(row) => ({
              cursor: 'pointer',
              backgroundColor:
                fileId === row?.file?.id
                  ? 'rgba(0, 255, 0, 0.08)'
                  : visitedAttachments.includes(row.id)
                  ? 'rgba(0, 0, 0, 0.05)'
                  : 'inherit',
              color: visitedAttachments.includes(row.id) ? 'purple' : 'inherit',
            })}
            onRowClick={(_, index) => {
              if (
                !disabledField &&
                hasPermission(UIElement.Attachment.EDIT_ATTACHMENT)
              ) {
                tableRef.current?.setSelectedIndex(index);
                editRef.current?.executeAction();
              }
            }}
          />
        )}
      </FormPanel>
      <Delimeter />
    </>
  );
}

function DetailDialogFields({
  documentId,
  documentType,
}: {
  documentId: string;
  documentType?: DocumentType;
}) {
  const { addVisitedAttachment } = useAttachmentContext();

  const id = useFormSelector(({ id }: { id: string }) => id);

  useEffect(() => {
    addVisitedAttachment(id);
  }, [id]);

  return (
    <AttachmentDialog documentType={documentType} documentId={documentId} />
  );
}
